import cx from 'classnames'
import { type ChangeEvent, useContext, useMemo } from 'react'

import { getPriceWithCurrency } from '@lib/format'
import { getIsValidProductQuantity } from '@lib/helpers'
import { ShopContext } from '@lib/shop-context'
import { UserContext } from '@lib/user/context'
import { FrameBuilderStringsContext } from '@frame-builder/contexts/strings-context'

interface PricingControlsProps {
  price: number
  quantity: string
  setQuantity: (quantity: string) => void
  className?: string
}

const PricingControls = ({
  price,
  quantity,
  setQuantity,
  className,
}: PricingControlsProps) => {
  const { currency, taxRate } = useContext(ShopContext)
  const frameBuilderStrings = useContext(FrameBuilderStringsContext)
  const { user } = useContext(UserContext)

  const isValidProductQuantity = useMemo(() => {
    return getIsValidProductQuantity(quantity)
  }, [quantity])

  const priceString = useMemo(() => {
    if (!price && price !== 0) {
      return
    }

    const priceMinotUnits = price * 100

    // Use only user company discount ignoring user product and product category discounts
    const companyDiscountRate = user?.company?.percentDiscount
      ? user.company.percentDiscount / 100
      : undefined

    const priceWithCurrency = getPriceWithCurrency(priceMinotUnits, currency, {
      discountRate: companyDiscountRate,
      taxRate,
      showCurrency: true,
      hasTrailingZeros: true,
      thousandSeparator: ' ',
    })

    return priceWithCurrency
  }, [currency, taxRate, price, user])

  const handleQuantityChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuantity(event.target.value)
  }

  return (
    <div
      className={cx(
        'flex justify-stretch items-stretch gap-5 px-3 bg-white',
        className,
      )}
    >
      <div className="flex-1 flex justify-center items-center pb-1">
        <label
          htmlFor="frame-builder-product-quantity"
          className="text-sm uppercase"
        >
          {frameBuilderStrings.controlQuantityLabel}
        </label>

        <input
          id="frame-builder-product-quantity"
          type="number"
          min="1"
          max="9999"
          value={quantity}
          onChange={handleQuantityChange}
          className={cx(
            'max-w-[80px] ml-2 py-2 px-2 border rounded-lg text-center',
            {
              'border-input-border': isValidProductQuantity,
              'border-error': !isValidProductQuantity,
            },
          )}
        />
      </div>

      <div className="flex-1 flex justify-center">
        <div className="flex flex-col justify-center">
          {!!priceString && (
            <span className="text-lg font-medium whitespace-nowrap">
              {priceString}
            </span>
          )}

          <div className="text-sm text-gray-700">
            {frameBuilderStrings.controlPriceLabel}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PricingControls
